import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { clearGameState } from '../utils/storage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          const userData = await api.getCurrentUser(storedToken);
          setUser(userData);
          setToken(storedToken);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('auth_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const register = async (email, password) => {
    try {
      const response = await api.register(email, password);
      const { access_token } = response;
      
      localStorage.setItem('auth_token', access_token);
      setToken(access_token);
      
      // Get user info
      const userData = await api.getCurrentUser(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Registration failed' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      const { access_token } = response;
      
      localStorage.setItem('auth_token', access_token);
      setToken(access_token);
      
      // Get user info
      const userData = await api.getCurrentUser(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    clearGameState();  // Clear any game data from localStorage
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setStats(null);
    window.location.reload();  // Reload for fresh guest start
  };

  const refreshStats = useCallback(async () => {
    if (!token) return;

    try {
      const userStats = await api.getUserStats(token);
      setStats(userStats);
    } catch (error) {
      console.error('Failed to refresh stats:', error);
    }
  }, [token]);

  const syncGameProgress = useCallback(async (date, guesses, won, guessCount) => {
    if (!token) return;

    try {
      await api.saveGameProgress(token, {
        date,
        guesses,
        won,
        guess_count: guessCount
      });
      
      // Refresh stats after saving
      await refreshStats();
    } catch (error) {
      console.error('Failed to sync game progress:', error);
    }
  }, [token, refreshStats]);

  const migrateLocalData = useCallback(async (gameHistory) => {
    if (!token) return;

    try {
      const response = await api.migrateData(token, gameHistory);
      await refreshStats();
      return response;
    } catch (error) {
      console.error('Failed to migrate data:', error);
      throw error;
    }
  }, [token, refreshStats]);

  const checkGameCompletion = useCallback(async (date) => {
    if (!token) return null;

    try {
      return await api.checkGameCompletion(token, date);
    } catch (error) {
      console.error('Failed to check game completion:', error);
      return null;
    }
  }, [token]);

  const loadGameProgress = useCallback(async (date) => {
    if (!token) return null;

    try {
      return await api.loadGameProgress(token, date);
    } catch (error) {
      console.error('Failed to load game progress:', error);
      return null;
    }
  }, [token]);

  const value = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading,
    stats,
    register,
    login,
    logout,
    syncGameProgress,
    migrateLocalData,
    refreshStats,
    loadGameProgress,
    checkGameCompletion
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
